import { useCallback, useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Chip } from '@amzn/stencil-react-components/chip';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@amzn/stencil-react-components/toggle-button';
// standards-ignore: cross-feature — IconPlusSmall is a generic "add" affordance; a shared wrapper over a bare icon would add indirection without value
import { IconGlobeSmall, IconPlusSmall } from '@amzn/stencil-react-icons';

import { useMessages } from '../../../shared/i18n/useMessages';
import { FieldErrorText } from '../../../shared/ui/forms/FieldErrorText';
import { isValidPolicyUrl } from '../logic/policyUrlValidation';

const SOP_BUNDLE = 'features.audit-whiz.translations.generateQuestions';

type SopKey =
  | 'sopSectionLabel'
  | 'sopOptionalTag'
  | 'sopSectionDescription'
  | 'sopSelectedLabel'
  | 'sopTabLink'
  | 'sopTabUpload'
  | 'sopLinkInputPlaceholder'
  | 'sopLinkInputAriaLabel'
  | 'sopLinkInvalidFormat'
  | 'sopLinkAddButton'
  | 'sopLinkPendingHint'
  | 'sopLinkBadgeSource'
  | 'sopLinkRemoveAltText'
  | 'sopUploadPlaceholder';

/** The two ways to attach an SOP reference. Only `link` is functional in
 *  the Phase 1 shell; `upload` renders placeholder copy. */
export type SopTab = 'link' | 'upload';

const TAB_ORDER: readonly SopTab[] = ['link', 'upload'];

// A Map (not an object-keyed record) so the union-typed `tab` lookup
// doesn't trip security/detect-object-injection.
const TAB_LABEL_KEY = new Map<SopTab, SopKey>([
  ['link', 'sopTabLink'],
  ['upload', 'sopTabUpload'],
]);

export interface SopReferenceSectionProps {
  /** The currently attached SOP link, or null when none is set. */
  readonly link: string | null;
  /** Called when the user adds an SOP link. */
  readonly onAddLink: (url: string) => void;
  /** Called when the user removes the attached SOP link. */
  readonly onRemoveLink: () => void;
  /** data-testid prefix for the section's interactive controls. */
  readonly dataTestId?: string;
}

/**
 * The "SOP reference (optional)" block inside the merged "Define your audit
 * questions" card. A three-way source selector (Link / Search Policy / Upload)
 * rendered as a single-select `ToggleButtonGroup` — the honest control for a
 * mutually-exclusive method choice whose selection becomes a value.
 *
 * Card-less: the merged card (owned by the page) supplies the card title and
 * the Source badge. Only the Link option is functional in the Phase 1 shell —
 * the auditor pastes a URL and adds it, at which point the section shows a
 * "Selected SOP" chip. Search and Upload show "coming soon" placeholders.
 *
 * The attached link lives in page state (via `link` / `onAddLink` /
 * `onRemoveLink`); only the local draft URL and the active tab are owned
 * here.
 */
export function SopReferenceSection({
  link,
  onAddLink,
  onRemoveLink,
  dataTestId,
}: SopReferenceSectionProps): JSX.Element {
  const { t } = useMessages<SopKey>(SOP_BUNDLE);
  const [activeTab, setActiveTab] = useState<SopTab>('link');
  const [draftUrl, setDraftUrl] = useState('');

  const hasLink = link !== null;
  const trimmedDraft = draftUrl.trim();
  const draftIsValid = isValidPolicyUrl(trimmedDraft);
  // Show the format error only once the user has typed something off-template;
  // an empty field is not an error. Derived (not stored) so it clears the
  // moment the draft becomes a valid policy link.
  const showFormatError = trimmedDraft.length > 0 && !draftIsValid;
  const canAdd = draftIsValid && !hasLink;
  // The user has typed a valid policy link but hasn't clicked Add yet. Surface a
  // reminder (and promote the Add button) so the pending link isn't silently
  // lost when they generate — the Generate action only reads committed links.
  const showPendingHint = canAdd;
  // Id linking the URL input to its error text via aria-describedby, so a
  // screen reader announces WHY the field is invalid (not just that it is).
  const errorId = dataTestId ? `${dataTestId}-link-error` : 'sop-link-error';
  // Suffix a stable data-testid for a control, or undefined when the section
  // has no id prefix. Keeps the JSX free of repeated `dataTestId ? … : …`.
  const tid = (suffix: string): string | undefined =>
    dataTestId ? `${dataTestId}-${suffix}` : undefined;

  const handleTabChange = useCallback(
    (_e: React.FormEvent<HTMLElement>, value: string | string[] | undefined) => {
      if (typeof value === 'string') setActiveTab(value as SopTab);
    },
    [],
  );

  const handleAddLink = useCallback(() => {
    const url = draftUrl.trim();
    // Guard on the template (defence-in-depth behind `canAdd`): only a valid
    // policy link can be attached, so an off-template URL never enters state.
    if (!isValidPolicyUrl(url)) return;
    onAddLink(url);
    setDraftUrl('');
  }, [draftUrl, onAddLink]);

  const handleUrlKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddLink();
      }
    },
    [handleAddLink],
  );

  return (
    <Col gridGap="dimensions.spacing.200" width="100%">
      {hasLink ? (
        <Col gridGap="dimensions.spacing.100">
          <Text fontWeight="bold">{t('sopSelectedLabel')}</Text>
          <Row gridGap="dimensions.spacing.200" flexWrap="wrap">
            <Chip
              icon={<IconGlobeSmall aria-hidden="true" />}
              onDismiss={onRemoveLink}
              dismissButtonAltText={t('sopLinkRemoveAltText')}
              data-testid={tid('link-badge')}
            >
              <Col>
                <Text>{link}</Text>
                <Text fontSize="T100" color="color.text.secondary">
                  {t('sopLinkBadgeSource')}
                </Text>
              </Col>
            </Chip>
          </Row>
        </Col>
      ) : (
        <>
          <Col gridGap="dimensions.spacing.50">
            <Row alignItems="center" gridGap="dimensions.spacing.50">
              <Text fontWeight="bold">{t('sopSectionLabel')}</Text>
              <Text color="color.text.secondary">{t('sopOptionalTag')}</Text>
            </Row>
            <Text fontSize="T100" color="color.text.secondary">
              {t('sopSectionDescription')}
            </Text>
          </Col>

          {/* Single-select source selector (segmented control) */}
          <ToggleButtonGroup value={activeTab} onChange={handleTabChange}>
            {TAB_ORDER.map((tab) => (
              <ToggleButton key={tab} value={tab} data-testid={tid(`tab-${tab}`)}>
                {t(TAB_LABEL_KEY.get(tab) ?? 'sopTabLink')}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {activeTab === 'link' && (
            <Col gridGap="dimensions.spacing.50">
              <Row gridGap="dimensions.spacing.200" alignItems="center">
                {/* The policy link follows a fixed ~50-char template, so the
                    input is capped rather than stretched edge-to-edge. */}
                <Col width="40%" minWidth={0}>
                  <Input
                    type="url"
                    width="100%"
                    value={draftUrl}
                    onChange={(e) => setDraftUrl(e.target.value)}
                    onKeyDown={handleUrlKeyDown}
                    placeholder={t('sopLinkInputPlaceholder')}
                    aria-label={t('sopLinkInputAriaLabel')}
                    aria-invalid={showFormatError}
                    {...(showFormatError ? { 'aria-describedby': errorId } : {})}
                    data-testid={tid('link-input')}
                  />
                </Col>
                <Button
                  size={ButtonSize.Small}
                  // Promote to Primary while a valid link is pending so the
                  // "Add link" step reads as the obvious next action.
                  variant={canAdd ? ButtonVariant.Primary : ButtonVariant.Secondary}
                  icon={<IconPlusSmall aria-hidden="true" />}
                  disabled={!canAdd}
                  onClick={handleAddLink}
                  data-testid={tid('link-add')}
                >
                  {t('sopLinkAddButton')}
                </Button>
              </Row>
              {showFormatError ? (
                <FieldErrorText
                  id={errorId}
                  {...(dataTestId ? { dataTestId: `${dataTestId}-link-error` } : {})}
                >
                  {t('sopLinkInvalidFormat')}
                </FieldErrorText>
              ) : null}
              {showPendingHint ? (
                <Row
                  alignItems="center"
                  gridGap="dimensions.spacing.50"
                  data-testid={tid('link-pending-hint')}
                >
                  <IconPlusSmall aria-hidden="true" />
                  <Text fontSize="T100" color="color.text.secondary">
                    {t('sopLinkPendingHint')}
                  </Text>
                </Row>
              ) : null}
            </Col>
          )}

          {activeTab === 'upload' && (
            <Text color="color.text.secondary" data-testid={tid('upload-placeholder')}>
              {t('sopUploadPlaceholder')}
            </Text>
          )}
        </>
      )}
    </Col>
  );
}
